// minimum spacing between a dropdown list and the viewport edges
const VIEWPORT_SPACING_IN_PX = 8

/**
 * @param {HTMLElement} listElement
 */
export function stickGroupLabelsToTop (listElement) {
  const listBounds = listElement.getBoundingClientRect()
  /** @type {HTMLElement[]} */
  const groupLabelElements = [
    ...listElement.querySelectorAll('.zb-dropdown-list-group-label')
  ]

  groupLabelElements.forEach(groupLabelElement => {
    /** @type {HTMLElement} */
    const elementContainingGroup = groupLabelElement.parentNode
    const elementContainingGroupBounds = elementContainingGroup.getBoundingClientRect()

    unStickElement(groupLabelElement)

    const groupIsPartiallyVisible =
      elementContainingGroupBounds.bottom > listBounds.top &&
      elementContainingGroupBounds.top < listBounds.top

    if (groupIsPartiallyVisible) {
      stickElementToTop(groupLabelElement, listElement, elementContainingGroup)
    }
  })
}

/**
 * @param {Element} element
 */
function unStickElement (element) {
  element.classList.remove('zb-dropdown-list-group-label-is-sticky')
}

/**
 * @param {HTMLElement} element
 * @param {HTMLElement} scrollingContainer
 * @param {HTMLElement} group
 */
function stickElementToTop (element, scrollingContainer, group) {
  const top = Math.min(
    scrollingContainer.scrollTop +
      scrollingContainer.offsetTop -
      element.offsetTop,
    group.offsetHeight - element.offsetHeight
  )

  element.style.top = top + 'px'
  element.classList.add('zb-dropdown-list-group-label-is-sticky')
}

/**
 * @param {HTMLElement} element
 * @param {HTMLElement} scrollingParent
 */
export function scrollIntoViewIfNeeded (element, scrollingParent) {
  const parentHeight = scrollingParent.offsetHeight
  const parentScrollTop = scrollingParent.scrollTop
  const elementOffsetTop = element.offsetTop - scrollingParent.offsetTop
  const elementOffsetBottom = element.offsetHeight + elementOffsetTop

  if (elementOffsetTop < parentScrollTop) {
    scrollingParent.scrollTop = elementOffsetTop
  } else if (elementOffsetBottom > parentScrollTop + parentHeight) {
    scrollingParent.scrollTop = elementOffsetBottom - parentHeight
  }
}

/**
 * @param {Element} componentElement
 */
export function scrollToSelectedItem (componentElement) {
  const selectedItemElement = componentElement.querySelector(
    '.zb-select-dropdown-list-item-is-selected'
  )
  const listElement = componentElement.querySelector('.zb-dropdown-list')
  if (selectedItemElement) {
    scrollIntoViewIfNeeded(selectedItemElement, listElement)
  }
}

/**
 * @param {Element} element
 * @param {Element} wrapperElement
 * @param {{align: string, offset: number}} position
 */
export function findOptimalWrapperOffset (element, wrapperElement, position) {
  const elementBounds = element.getBoundingClientRect()
  const viewportWidth = document.documentElement.clientWidth
  const wrapperWidth = wrapperElement.offsetWidth
  const rightBound = elementBounds.left + wrapperWidth + position.offset
  const leftBound = elementBounds.right - wrapperWidth - position.offset

  let newOffset

  if (position.align === 'right') {
    newOffset =
      leftBound < 0
        ? position.offset + leftBound - VIEWPORT_SPACING_IN_PX
        : position.offset
  } else {
    newOffset =
      rightBound > viewportWidth
        ? position.offset + viewportWidth - rightBound + VIEWPORT_SPACING_IN_PX
        : position.offset
  }

  return newOffset
}

/**
 * @template T, Item
 * @param {T[]} suggestions
 * @param {boolean} withGrouping
 * @param {(group: T) => Item[]} getGroupChildren
 * @param {(group: T, index: number) => import("react").ReactNode} renderGroupLabel
 * @returns {Item[] | { group: Item[], label: import("react").ReactNode}[]}
 */
export function getUnpackedSuggestions (
  suggestions,
  withGrouping,
  getGroupChildren,
  renderGroupLabel
) {
  return withGrouping ? suggestions.reduce(unpackSuggestions, []) : suggestions

  /**
   * @param {{group: Item[]: label: import("react").ReactNode}[]} values
   * @param {T} items
   * @param {number} index
   */
  function unpackSuggestions (values, items, index) {
    const group = getGroupChildren(items)
    return group.length
      ? values.concat({
        label: renderGroupLabel(items, index),
        group
      })
      : values
  }
}

/**
 * @template T, Item
 * @param {T[]} suggestions
 * @param {boolean} withGrouping
 * @param {(group: T) => Item[]} getGroupChildren
 * @param {(item: Item, index: number) => string} getSuggestionValue
 */
export function getFlattenedSuggestions (
  suggestions,
  withGrouping,
  getGroupChildren,
  getSuggestionValue
) {
  return (withGrouping
    ? suggestions.reduce(unpackSuggestions, [])
    : suggestions
  ).map(getSuggestionValue)

  /**
   * @param {Item[]} values
   * @param {T} items
   * @param {number} index
   */
  function unpackSuggestions (values, items) {
    const group = getGroupChildren(items)
    return group.length ? values.concat(group) : values
  }
}
/**
 * @template T, Item
 * @param {boolean} withGrouping
 * @param {T[]|{group: Item[]: label: import("react").ReactNode}[]} unpackedSuggestions
 * @returns {Item[]}
 */
export function getFlattenedUnpackedSuggestions (withGrouping, unpackedSuggestions) {
  return withGrouping
    ? unpackedSuggestions.reduce(
      /** @type {(groups: Item[], group: {group: Item[]: label: import("react").ReactNode}) => Item[]} */
      (groups, { group }) => groups.concat(group),
      []
    )
    : unpackedSuggestions
}
